<template>
  <PageContainer>
    <View direction="column" gap="sm">
      <PageBackNav :teleport-slot-id="headerTeleportSlot" @back-nav="goBack(showingsRoute())" />

      <View v-if="state.status === 'pending'" justify="center" padding="xl">
        <LoadingIndicator />
      </View>

      <EmptyPagePlaceholder
        v-else-if="state.status === 'error'"
        title="Could not load showing"
        description="Please reload the page or try again later"
      />

      <div v-else-if="showing" class="wrapper">
        <Card class="showing-card">
          <template #header>
            <Text variant="h2">{{ title }}</Text>
          </template>

          <View direction="column" gap="md">
            <View :grow="1" gap="sm" align="center">
              <PropertyPhotoPreview :image-url="showing.property.coverImage" size="sm" />
              <Text variant="label-medium">{{ showing.property.displayName }}</Text>
            </View>

            <ShowingRenters :showing="showing" />
          </View>
        </Card>

        <ShowingAgentCard v-if="showingAgent" :showing-agent="showingAgent" :showing-id="showing.id" />

        <Card class="controls-card">
          <View gap="sm" class="controls">
            <Button variant="secondary" :disabled="!isEditable" appearance="danger" @click="openCancelSrDialog">
              Cancel showing
            </Button>
            <Button variant="secondary" :disabled="!isEditable" @click="openRescheduleSrDialog">Reschedule</Button>
          </View>
        </Card>

        <Card class="aside">
          <template #header>
            <Text variant="h4">New showing requests</Text>
          </template>

          <View direction="column" gap="sm" v-if="pendingShowingRequests?.length">
            <ShowingRequestReviewCard
              v-for="showingRequest in pendingShowingRequests"
              :key="showingRequest.id"
              :renter-name="showingRequest.renter.user.name"
              :renter-id="showingRequest.renter.id"
              :property-id="showing.property?.id"
              :requested-at="showingRequest.createdAt"
            />
          </View>
          <Text v-else variant="body-medium" color="secondary">We will show new requests for this showing here</Text>
        </Card>

        <!-- Dialogs-->
        <CancelShowingModal :showing-id="showing.id" ref="cancelDialogRef" />
        <RescheduleShowingModal
          :showing-id="showing.id"
          :showing-agent-id="showing.showingAgent?.id"
          ref="rescheduleDialogRef"
        />
      </div>
    </View>
  </PageContainer>
</template>

<script setup lang="ts">
import { useDateFormat } from '@vueuse/core';
import { computed, ref } from 'vue';

import {
  Avatar,
  Button,
  Card,
  EmptyPagePlaceholder,
  LoadingIndicator,
  PageBackNav,
  PageContainer,
  Text,
  View,
} from '@tallo/design-system';
import { showingsRoute, useNavigation } from '@tallo/investor/navigation';
import {
  ShowingRequestStatus,
  CancelShowingModal,
  getShowingTime,
  RescheduleShowingModal,
  ShowingStatus,
  useShowingQuery,
  ShowingRequestReviewCard,
} from '@tallo/investor/showing';
import { PropertyPhotoPreview } from '@tallo/property';

import { headerTeleportSlot } from '../../../root/header-teleport-slot.const';
import ShowingAgentCard from './showing-agent-card.vue';
import ShowingRenters from './showing-renters.vue';

const { goBack } = useNavigation();

const rescheduleDialogRef = ref<typeof RescheduleShowingModal | null>(null);
const cancelDialogRef = ref<typeof CancelShowingModal | null>(null);

const { state } = useShowingQuery();

const showing = computed(() => state.value.data);
const showingAgent = computed(() => showing.value?.showingAgent);
const isEditable = computed(() => [ShowingStatus.PENDING, ShowingStatus.CONFIRMED].includes(showing.value?.status!));
const pendingShowingRequests = computed(() => {
  return showing.value?.showingRequests?.filter(({ status }) => status === ShowingRequestStatus.PENDING);
});

const title = computed(() => {
  const startTime = showing.value?.startTime;
  const endTime = showing.value?.endTime;

  if (!startTime || !endTime) {
    return '';
  }

  return `${useDateFormat(startTime, 'dddd').value}, ${getShowingTime(startTime, endTime, 'comma')}`;
});

function openRescheduleSrDialog() {
  rescheduleDialogRef.value!.open();
}

function openCancelSrDialog() {
  cancelDialogRef?.value!.open();
}
</script>

<style lang="scss" scoped>
@import 'styles/mixins/layout.mixins';

.wrapper {
  display: grid;
  grid-template-columns: auto 30rem;
  gap: var(--t-spacing-sm);

  @include screen-max-lg {
    grid-template-columns: 1fr;
    max-width: 100%;
  }
}

.aside {
  width: 100%;
  grid-area: 1 / 2 / 3 / 3;

  @include screen-max-lg {
    grid-area: 2;
  }
}

.controls {
  & > * {
    flex-grow: 1;
  }

  @include mobile {
    flex-direction: column-reverse;
    justify-content: end;
  }
}
</style>
